// Breakfast levels. Coordinates: 1200 x 800. The ball is a pea, the goal is the porridge bowl.
// The id is fixed (progress is saved under it) – never change it once the level is released.
import { defineLevels } from '../../core/registry';
import { on } from '../../core/level-helpers';
import type { LevelData, SkinType, StaticData } from '../../core/types';
import { P } from './shared';

// Walls and small stoppers use the unregistered skin 'wall', so they are drawn as the default lilac block
// (known finding in tests/validate-allow.json). A registered skin would change the golden world shape.
const WALL = 'wall' as SkinType;

const L: LevelData[] = [];

{ // 1 – First roll – build a bridge with a plank
  const s1: StaticData = { x: 220, y: 290, w: 360, h: 22, a: 9, skin: 'shelf' };
  const s2: StaticData = { x: 750, y: 390, w: 400, h: 22, a: 5, skin: 'shelf' };
  L.push({
    id: 'first-roll', par: 1, tools: { plank: 2 }, learn: ['gravity', 'ramp'],
    statics: [s1, s2, { x: 1040, y: 575, w: 240, h: 34, skin: 'counter' }, { x: 1150, y: 380, w: 40, h: 360, skin: 'fridge' }],
    balls: [on(s1, -150, P)],
    goal: { type: 'bowl', x: 1040, y: 551, w: 170 },
    objs: [{ t: 'duck', x: 1080, y: 420 }],
    solution: [{ t: 'plank', x: 480, y: 345, a: 15 }],
  });
}
{ // 2 – Bounce! – new tool: the pad (spring)
  const s1: StaticData = { x: 140, y: 200, w: 220, h: 20, a: 10, skin: 'shelf' };
  L.push({
    id: 'bounce', par: 1, tools: { pad: 1, plank: 1 }, learn: ['spring'], intro: 'pad',
    statics: [s1, { x: 900, y: 520, w: 340, h: 30, skin: 'counter' }, { x: 640, y: 680, w: 26, h: 320, skin: WALL }],
    balls: [on(s1, -80, P)],
    goal: { type: 'bowl', x: 920, y: 496, w: 160 },
    objs: [{ t: 'duck', x: 560, y: 260 }],
    solution: [{ t: 'pad', x: 400, y: 490, a: 15 }],
  });
}
{ // 3 – The banana – new tool: the banana turns the ball around
  const s1: StaticData = { x: 120, y: 150, w: 180, h: 20, a: 12, skin: 'shelf' };
  L.push({
    id: 'banana', par: 1, tools: { banana: 1, plank: 1 }, learn: ['ramp', 'direction'], intro: 'banana',
    statics: [s1, { x: 460, y: 640, w: 26, h: 340, skin: WALL }, { x: 610, y: 560, w: 260, h: 26, skin: 'counter' }, { x: 735, y: 390, w: 30, h: 320, skin: 'fridge' }],
    balls: [on(s1, -60, P)],
    goal: { type: 'bowl', x: 605, y: 537, w: 200 },
    objs: [{ t: 'duck', x: 560, y: 300 }],
    solution: [{ t: 'banana', x: 400, y: 400, a: 15 }],
  });
}
{ // 4 – The seesaw – new tool: the stone; drop it on the seesaw to launch the pea
  L.push({
    id: 'seesaw', par: 1, tools: { stone: 1, plank: 1 }, learn: ['lever', 'gravity'], intro: 'seesaw',
    statics: [{ x: 522, y: 585, w: 10, h: 40, skin: WALL }, { x: 400, y: 700, w: 60, h: 200, skin: 'leg' }],
    fixed: [{ t: 'seesaw', x: 400, y: 560, a: 20 }],
    balls: [{ x: 495, y: 570, r: P }],
    goal: { type: 'bell', x: 510, y: 380 },
    objs: [{ t: 'duck', x: 515, y: 300 }],
    solution: [{ t: 'stone', x: 310, y: 250, a: 0 }],
  });
}
{ // 5 – The paint pot – new tool: paint makes the ball sticky (friction)
  const s1: StaticData = { x: 300, y: 250, w: 520, h: 22, a: 25, skin: 'shelf' };
  L.push({
    id: 'paint-bucket', par: 1, tools: { paint: 1, pad: 1 }, learn: ['friction', 'materials'], intro: 'paint',
    statics: [s1, { x: 820, y: 660, w: 640, h: 30, skin: 'counter' }],
    balls: [on(s1, -230, P)],
    goal: { type: 'bowl', x: 610, y: 636, w: 160 },
    objs: [{ t: 'duck', x: 578, y: 362 }],
    solution: [{ t: 'paint', x: 420, y: 290, a: 0 }],
  });
}
{ // 6 – The toaster – new machine: the toaster pops the pea up
  const s1: StaticData = { x: 150, y: 300, w: 220, h: 20, a: 10, skin: 'shelf' };
  L.push({
    id: 'toaster', par: 1, tools: { plank: 2 }, learn: ['energy', 'mechanism'], intro: 'toaster',
    statics: [s1, { x: 570, y: 700, w: 220, h: 40, skin: 'counter' }, { x: 880, y: 420, w: 240, h: 26, skin: 'shelf' }],
    balls: [on(s1, -80, P)],
    goal: { type: 'bowl', x: 880, y: 397, w: 170 },
    objs: [{ t: 'toaster', x: 570, y: 640, pop: [4, -15] }, { t: 'duck', x: 740, y: 230 }],
    solution: [{ t: 'plank', x: 320, y: 450, a: 15 }],
  });
}
{ // 7 – Cut the rope – new machine: the button cuts the rope, the stone drops onto the seesaw
  const sb: StaticData = { x: 150, y: 150, w: 180, h: 20, a: 6, skin: 'shelf' };
  L.push({
    id: 'cut-the-rope', par: 1, tools: { plank: 2 }, learn: ['chain_reaction', 'mechanism'], intro: 'button',
    statics: [sb, { x: 520, y: 430, w: 170, h: 20, skin: 'shelf' }, { x: 598, y: 400, w: 14, h: 60, skin: WALL }, { x: 636, y: 668, w: 10, h: 40, skin: WALL }, { x: 760, y: 758, w: 50, h: 180, skin: 'leg' }],
    fixed: [{ t: 'seesaw', x: 760, y: 640, a: -20 }],
    balls: [{ x: 665, y: 650, r: P }, on(sb, -70, 13, { helper: true })],
    goal: { type: 'bell', x: 648, y: 470 },
    objs: [{ t: 'button', x: 555, y: 405, act: { cut: 'r1' } }, { t: 'rope', id: 'r1', ax: 870, ay: 120, hang: { t: 'stone', x: 870, y: 300 } }, { t: 'duck', x: 330, y: 195 }],
    solution: [{ t: 'plank', x: 340, y: 260, a: 15 }],
  });
}
{ // 8 – Three peas – all three peas must reach the bowl
  const s1: StaticData = { x: 220, y: 200, w: 360, h: 20, a: 10, skin: 'shelf' };
  L.push({
    id: 'three-peas', par: 1, tools: { plank: 3 }, learn: ['counting', 'ramp'],
    statics: [s1, { x: 700, y: 480, w: 240, h: 20, a: 6, skin: 'shelf' }, { x: 1000, y: 680, w: 260, h: 30, skin: 'counter' }, { x: 1150, y: 520, w: 40, h: 360, skin: 'fridge' }],
    balls: [on(s1, -150, P), on(s1, -110, P), on(s1, -70, P)],
    goal: { type: 'bowl', x: 1000, y: 656, w: 190, count: 3 },
    objs: [{ t: 'duck', x: 786, y: 436 }],
    solution: [{ t: 'plank', x: 520, y: 366, a: 15 }],
  });
}
{ // 9 – The window – bounce the pea through the gap between the walls
  const s1: StaticData = { x: 130, y: 110, w: 200, h: 20, a: 10, skin: 'shelf' };
  L.push({
    id: 'window', par: 1, tools: { pad: 2, plank: 1 }, learn: ['spring', 'energy'],
    statics: [s1, { x: 560, y: 590, w: 30, h: 420, skin: WALL }, { x: 560, y: 60, w: 30, h: 120, skin: WALL }, { x: 900, y: 470, w: 300, h: 24, skin: 'shelf' }, { x: 1065, y: 330, w: 30, h: 260, skin: 'fridge' }],
    balls: [on(s1, -70, P)],
    goal: { type: 'bowl', x: 930, y: 446, w: 190 },
    objs: [{ t: 'duck', x: 560, y: 250 }],
    solution: [{ t: 'pad', x: 380, y: 400, a: 15 }],
  });
}
{ // 10 – The pulley – new machine: a stone in the bucket lifts the gate
  const s1: StaticData = { x: 360, y: 400, w: 640, h: 22, a: 10, skin: 'shelf' };
  L.push({
    id: 'pulley', par: 1, tools: { stone: 1, plank: 1 }, learn: ['pulley', 'weight'], intro: 'pulley',
    statics: [s1, { x: 760, y: 674, w: 280, h: 30, skin: 'counter' }, { x: 872, y: 560, w: 30, h: 200, skin: 'fridge' }],
    balls: [on(s1, -250, P)],
    goal: { type: 'bowl', x: 760, y: 650, w: 190 },
    objs: [{ t: 'pulley', x: 725, y: 110, A: { x: 470, y: 330, type: 'gate', h: 150 }, B: { x: 1000, y: 280, type: 'bucket' }, min: 0, range: 170 }, { t: 'duck', x: 633, y: 398 }],
    solution: [{ t: 'stone', x: 1000, y: 120, a: 0 }],
  });
}
{ // 11 – Chain reaction – button → rope → stone → pulley opens the gate
  const sb: StaticData = { x: 110, y: 160, w: 170, h: 20, a: 5, skin: 'shelf' };
  const sp: StaticData = { x: 1040, y: 430, w: 300, h: 22, a: -10, skin: 'shelf' };
  const sq: StaticData = { x: 560, y: 630, w: 300, h: 22, a: -8, skin: 'shelf' };
  L.push({
    id: 'chain-reaction', par: 2, tools: { plank: 3 }, learn: ['chain_reaction', 'pulley', 'weight'],
    statics: [sb, sp, sq, { x: 400, y: 330, w: 170, h: 20, skin: 'shelf' }, { x: 478, y: 300, w: 14, h: 60, skin: WALL }, { x: 420, y: 760, w: 260, h: 30, skin: 'counter' }, { x: 318, y: 640, w: 26, h: 220, skin: 'fridge' }],
    balls: [on(sp, 120, P), on(sb, -60, 13, { helper: true })],
    goal: { type: 'bowl', x: 440, y: 736, w: 190 },
    objs: [
      { t: 'button', x: 440, y: 305, act: { cut: 'r1' } },
      { t: 'rope', id: 'r1', ax: 700, ay: 60, hang: { t: 'stone', x: 700, y: 170 } },
      { t: 'pulley', x: 850, y: 90, A: { x: 1000, y: 356, type: 'gate', h: 150 }, B: { x: 700, y: 300, type: 'bucket' }, min: 0, range: 170 },
      { t: 'duck', x: 250, y: 600 },
    ],
    solution: [{ t: 'plank', x: 300, y: 260, a: 15 }, { t: 'plank', x: 820, y: 540, a: -15 }],
  });
}
{ // 12 – The morning bell (finale) – toaster, pad, button and seesaw in one chain
  const s1: StaticData = { x: 150, y: 250, w: 260, h: 20, a: 12, skin: 'shelf' };
  L.push({
    id: 'morning-bell', par: 2, tools: { plank: 2, pad: 1 }, learn: ['chain_reaction', 'lever', 'spring'],
    statics: [s1, { x: 300, y: 750, w: 220, h: 40, skin: 'counter' }, { x: 830, y: 440, w: 140, h: 20, a: -6, skin: 'shelf' }, { x: 757, y: 415, w: 14, h: 70, skin: WALL }, { x: 1153, y: 668, w: 10, h: 40, skin: WALL }, { x: 1030, y: 758, w: 50, h: 180, skin: 'leg' }],
    fixed: [{ t: 'seesaw', x: 1030, y: 640, a: 20 }],
    balls: [on(s1, -100, P), { x: 1124, y: 652, r: 13, helper: true }],
    goal: { type: 'bell', x: 1115, y: 470, any: true },
    objs: [
      { t: 'toaster', x: 300, y: 690, pop: [3, -17], funnel: true },
      { t: 'button', x: 796, y: 418, a: -6, act: { cut: 'r1' } },
      { t: 'rope', id: 'r1', ax: 925, ay: 200, hang: { t: 'stone', x: 925, y: 360 } },
      { t: 'duck', x: 367, y: 300 },
    ],
    solution: [{ t: 'plank', x: 460, y: 440, a: -30 }, { t: 'pad', x: 620, y: 610, a: 0 }],
  });
}

export const levels = defineLevels(L);
